import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Printer, AlertTriangle, FlaskConical, Activity } from "lucide-react";
import { fmtDate } from "../lib/api";

/**
 * Printable "urgent corrective actions" report. Renders cleanly on A4,
 * suppresses interactive chrome via print CSS, and includes Claude's
 * cached explanations when available.
 *
 * Layout:
 *   header (clinic name, date)
 *   numbered list — one card per athlete-issue with markers, last 2
 *   values, range, and the templated corrective action text.
 */
export default function UrgentActionsReport({ items, generatedAt, onClose }) {
  // Auto-trigger print dialog after first paint so the operator doesn't
  // have to hunt for the menu.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        window.print();
      } catch (err) {
        console.warn("[urgent-report] print() failed:", err?.message);
      }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  return createPortal(
    <div className="urgent-print-shell fixed inset-0 z-[210] bg-white text-black overflow-y-auto" data-testid="urgent-print-report">
      {/* Toolbar — hidden on print */}
      <div className="urgent-print-toolbar sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-300 bg-white">
        <div className="text-sm font-semibold text-slate-700">
          Urgent corrective actions — {items.length} item{items.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-400 text-slate-700 hover:bg-slate-100 text-sm"
            data-testid="urgent-report-print-btn"
          >
            <Printer size={13} /> Print
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-400 text-slate-700 hover:bg-slate-100 text-sm"
            data-testid="urgent-report-close-btn"
          >
            <X size={13} /> Close
          </button>
        </div>
      </div>

      <div className="urgent-print-content max-w-[850px] mx-auto px-8 py-8">
        <div className="border-b-2 border-slate-800 pb-3 mb-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            The Yacht Club of Hyderabad · Athlete Health
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Urgent corrective actions</h1>
          <div className="text-xs text-slate-600 mt-1 font-mono">
            Generated {generatedAt ? fmtDate(generatedAt.slice(0, 10)) : "—"} · {items.length} pending review
          </div>
        </div>

        <div className="text-xs text-slate-600 mb-5 leading-relaxed">
          The following items were flagged as <strong>persistent</strong> (the same marker
          appeared in 2+ consecutive lab tests) and <strong>alarming</strong>
          (more than 25% beyond the reference range edge) or are active
          injuries. Items are ranked by severity × persistence (or
          severity × days-active for injuries). Tick each one in the dashboard
          once you&apos;ve acted on it.
        </div>

        <div className="space-y-4">
          {items.map((it, idx) => (
            <ReportItem key={it.issue_key} rank={idx + 1} item={it} />
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-slate-300 text-[10px] text-slate-500 leading-snug">
          Corrective-action suggestions are <strong>not</strong> a substitute for
          physician consultation. Confirm with the squad physician before
          any change to supplementation, training load, or medical care.
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .urgent-print-toolbar { display: none !important; }
          .urgent-print-shell { position: static !important; }
          .urgent-print-content { padding: 0 !important; }
          .urgent-item { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

function ReportItem({ rank, item }) {
  const isLab = item.kind === "lab";
  return (
    <div className="urgent-item rounded border border-slate-300 p-3" data-testid={`urgent-report-item-${item.issue_key}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-bold text-slate-900">
          <span className="text-slate-500 mr-2">#{rank}</span>
          {item.sailor_name}
          <span className="text-[11px] font-normal text-slate-500 ml-2">· {item.sailor_group}</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <span className={`px-1.5 py-0.5 rounded border ${
            isLab
              ? "bg-amber-100 text-amber-900 border-amber-400"
              : "bg-rose-100 text-rose-900 border-rose-400"
          }`}>
            {isLab ? <><FlaskConical size={9} className="inline -mt-0.5" /> LAB</>
                   : <><Activity size={9} className="inline -mt-0.5" /> INJURY</>}
          </span>
          <span className="text-slate-700">score {item.score}</span>
        </div>
      </div>

      {isLab ? <LabReport item={item} /> : <InjuryReport item={item} />}

      <div className="mt-2 border-t border-slate-200 pt-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
          Suggested corrective action
        </div>
        <div className="text-[12px] text-slate-800 leading-snug">{item.corrective_action}</div>
      </div>

      <div className="mt-2 text-[10px] text-slate-400 italic">
        ☐ Reviewed · ☐ Communicated to athlete · ☐ Action started · Next review: __________
      </div>
    </div>
  );
}

function LabReport({ item }) {
  const range = `${item.range_low ?? "—"}–${item.range_high ?? "—"} ${item.param_unit || ""}`.trim();
  return (
    <>
      <div className="mt-1 text-[13px] text-slate-800">
        <span className="font-semibold">{item.param_label}</span>{" "}
        <span className="text-slate-500">({item.param_group})</span>
        <span className="ml-2 text-rose-700 text-xs font-mono">
          {item.status === "high" ? "↑ HIGH" : "↓ LOW"} · {Math.abs(item.deviation_pct)}% beyond range
        </span>
      </div>
      <div className="mt-1 text-[11px] text-slate-700 font-mono">
        Recent: {(item.last_values || []).map((v) => `${(v.date || "").slice(2, 10)} = ${v.value}`).join("  ·  ")}
        {"   "}
        <span className="text-slate-500">Range {range}</span>
      </div>
      <div className="text-[11px] text-slate-600 mt-1">
        Persistence: this marker has been {item.status === "high" ? "elevated" : "depressed"} in the
        last <strong>{item.persistence}</strong> consecutive lab tests.
      </div>
    </>
  );
}

function InjuryReport({ item }) {
  const bodyPart =
    item.body_part === "general" && item.body_part_other
      ? item.body_part_other
      : (item.body_part || "").replace(/_/g, " ");
  const typeStr =
    item.injury_type === "other" && item.injury_type_other
      ? item.injury_type_other
      : (item.injury_type || "").replace(/_/g, " ");
  return (
    <>
      <div className="mt-1 text-[13px] text-slate-800">
        <span className="font-semibold capitalize">{typeStr}</span>
        {bodyPart && <> · <span className="text-slate-700">{bodyPart}</span></>}
        {item.severity && (
          <span className="ml-2 text-rose-700 text-xs font-mono uppercase">
            {item.severity.replace(/_/g, " ")}
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-slate-700 font-mono">
        Onset: {fmtDate(item.onset_date)} · {item.days_active} days active · status: {item.status}
      </div>
      {item.description && (
        <div className="mt-1 text-[11px] text-slate-700 italic">&ldquo;{item.description}&rdquo;</div>
      )}
      {item.treatment && (
        <div className="text-[11px] text-slate-700">Treatment so far: {item.treatment}</div>
      )}
    </>
  );
}
